import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Tooltip, Divider, Popover } from '@mui/material';
import {
  Add as AddIcon,
  NearMe as CursorIcon,
  PanTool as PanIcon,
  Create as PencilIcon,
  Timeline as LineIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  CenterFocusWeak as ResetZoomIcon,
} from '@mui/icons-material';
import { useCorkboard, TOOL_MODES } from './CorkboardProvider';
import corkboardColors from './corkboardColors.json';
import useThemeStyles from '../../hooks/useThemeStyles';

const { lineColors } = corkboardColors;

// Get array of color keys for the picker UI
const LINE_COLOR_KEYS = Object.keys(lineColors);

/**
 * Resolve a color key to its actual hex value based on theme
 */
const resolveLineColor = (colorKey, isDarkMode) => {
  if (!colorKey) return isDarkMode ? '#ffffff' : '#000000';
  const colorEntry = lineColors[colorKey];
  if (colorEntry) {
    return isDarkMode ? colorEntry.dark : colorEntry.light;
  }
  // Fallback: if it's already a hex color, return as-is
  if (colorKey.startsWith('#') || colorKey.startsWith('rgb')) {
    return colorKey;
  }
  return isDarkMode ? '#ffffff' : '#000000';
};

// Define button components outside to avoid nested component issues
const ToolButton = ({ icon, tooltip, isActive, onClick, disabled }) => (
  <Tooltip title={tooltip} placement="top">
    <span>
      <IconButton
        onClick={onClick}
        disabled={disabled}
        sx={{
          backgroundColor: isActive ? 'primary.main' : 'background.paper',
          color: isActive ? 'primary.contrastText' : 'text.primary',
          '&:hover': {
            backgroundColor: isActive ? 'primary.dark' : 'action.hover',
          },
          '&:disabled': {
            backgroundColor: 'action.disabledBackground',
            color: 'action.disabled',
          },
          width: 40,
          height: 40,
        }}
      >
        {icon}
      </IconButton>
    </span>
  </Tooltip>
);

ToolButton.propTypes = {
  icon: PropTypes.node.isRequired,
  tooltip: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

ToolButton.defaultProps = {
  isActive: false,
  disabled: false,
};

const ColorButton = ({ icon, tooltip, isActive, onClick, color }) => (
  <Tooltip title={tooltip} placement="top">
    <Box sx={{ position: 'relative' }}>
      <IconButton
        onClick={onClick}
        sx={{
          backgroundColor: isActive ? 'primary.main' : 'background.paper',
          color: isActive ? 'primary.contrastText' : 'text.primary',
          '&:hover': {
            backgroundColor: isActive ? 'primary.dark' : 'action.hover',
          },
          width: 40,
          height: 40,
        }}
      >
        {icon}
      </IconButton>
      <Box
        sx={{
          position: 'absolute',
          bottom: 2,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 20,
          height: 4,
          backgroundColor: color,
          borderRadius: 1,
          border: '1px solid rgba(0,0,0,0.2)',
        }}
      />
    </Box>
  </Tooltip>
);

ColorButton.propTypes = {
  icon: PropTypes.node.isRequired,
  tooltip: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  color: PropTypes.string.isRequired,
};

ColorButton.defaultProps = {
  isActive: false,
};

/**
 * CorkboardHotbar - Fixed toolbar at bottom center with all corkboard tools
 */
const CorkboardHotbar = () => {
  const { isDark } = useThemeStyles();
  const {
    toolMode,
    setToolMode,
    drawColor,
    setDrawColor,
    undo,
    redo,
    canUndo,
    canRedo,
    resetZoom,
    isPhantomNoteMode,
    startPhantomNoteMode,
    cancelPhantomNoteMode,
  } = useCorkboard();

  const [colorAnchor, setColorAnchor] = useState(null);

  const handleColorClick = (e) => {
    setColorAnchor(e.currentTarget);
  };

  const handleColorClose = () => {
    setColorAnchor(null);
  };

  const handleColorSelect = (color) => {
    setDrawColor(color);
    handleColorClose();
  };

  const handleAddNote = () => {
    // Toggle phantom note mode
    if (isPhantomNoteMode) {
      cancelPhantomNoteMode();
    } else {
      startPhantomNoteMode();
    }
  };

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: isDark ? 'rgba(50, 50, 50, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          borderRadius: 2,
          boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.15)',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          p: 1,
          zIndex: 1000,
        }}
      >
        {/* Add Note */}
        <ToolButton
          icon={<AddIcon />}
          tooltip={isPhantomNoteMode ? 'Cancel Add Note (Click to place)' : 'Add Note'}
          isActive={isPhantomNoteMode}
          onClick={handleAddNote}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Cursor Mode */}
        <ToolButton
          icon={<CursorIcon />}
          tooltip="Cursor (Select/Move)"
          isActive={toolMode === TOOL_MODES.CURSOR}
          onClick={() => setToolMode(TOOL_MODES.CURSOR)}
        />

        {/* Pan Mode */}
        <ToolButton
          icon={<PanIcon />}
          tooltip="Pan Canvas"
          isActive={toolMode === TOOL_MODES.PAN}
          onClick={() => setToolMode(TOOL_MODES.PAN)}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Pencil Mode */}
        <ColorButton
          icon={<PencilIcon />}
          tooltip="Freehand Draw"
          isActive={toolMode === TOOL_MODES.PENCIL}
          onClick={(e) => {
            if (toolMode === TOOL_MODES.PENCIL) {
              handleColorClick(e);
            } else {
              setToolMode(TOOL_MODES.PENCIL);
            }
          }}
          color={resolveLineColor(drawColor, isDark)}
        />

        {/* Line Mode */}
        <ColorButton
          icon={<LineIcon />}
          tooltip="Draw Line"
          isActive={toolMode === TOOL_MODES.LINE}
          onClick={(e) => {
            if (toolMode === TOOL_MODES.LINE) {
              handleColorClick(e);
            } else {
              setToolMode(TOOL_MODES.LINE);
            }
          }}
          color={resolveLineColor(drawColor, isDark)}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Undo */}
        <ToolButton icon={<UndoIcon />} tooltip="Undo" onClick={undo} disabled={!canUndo} />

        {/* Redo */}
        <ToolButton icon={<RedoIcon />} tooltip="Redo" onClick={redo} disabled={!canRedo} />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Reset Zoom */}
        <ToolButton icon={<ResetZoomIcon />} tooltip="Reset Zoom & Pan" onClick={resetZoom} />
      </Box>

      {/* Color Picker Popover */}
      <Popover
        open={Boolean(colorAnchor)}
        anchorEl={colorAnchor}
        onClose={handleColorClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            backgroundColor: isDark ? '#2d2d2d' : '#ffffff',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          },
        }}
      >
        <Box sx={{ p: 1.5, display: 'flex', flexWrap: 'wrap', width: 200, gap: 0.5 }}>
          {LINE_COLOR_KEYS.map((colorKey) => {
            const displayColor = resolveLineColor(colorKey, isDark);
            const isSelected = drawColor === colorKey;
            return (
              <Box
                key={colorKey}
                onClick={() => handleColorSelect(colorKey)}
                title={colorKey}
                sx={{
                  width: 28,
                  height: 28,
                  backgroundColor: displayColor,
                  borderRadius: 0.5,
                  cursor: 'pointer',
                  border: isSelected
                    ? '2px solid #1976d2'
                    : `1px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}`,
                  '&:hover': {
                    transform: 'scale(1.1)',
                  },
                }}
              />
            );
          })}
        </Box>
      </Popover>
    </>
  );
};

export default CorkboardHotbar;
